import Vue from 'vue'
import Vuex from 'vuex'

import { GraphQLClient } from 'graphql-request'

import Hotsite from './graphql/queries/hotsite.query.gql'
import UserExists from './graphql/queries/userexists.query.gql'
import CreateUser from './graphql/queries/createuser.query.gql'
import Login from './graphql/queries/login.query.gql'
import TokenLogin from './graphql/queries/tokenLogin.query.gql'
import ValidatePassword from './graphql/queries/validatePassword.query.gql'
import UpdatePassword from './graphql/queries/updatePassword.query.gql'
import UpdateUser from './graphql/queries/updateUser.query.gql'
import PasswordRecover from './graphql/queries/passwordRecover.query.gql'
import PasswordRecoverSms from './graphql/queries/passwordRecoverSms.query.gql'
import ResetPassword from './graphql/queries/resetPassword.query.gql'
import ResetPasswordSms from './graphql/queries/resetPasswordSms.query.gql'
import TokenValidation from './graphql/queries/tokenValidation.query.gql'
import PhoneValidation from './graphql/queries/phoneValidation.query.gql'
import CreateDeleteDataRequest from './graphql/queries/createDeleteDataRequest.query.gql'
import ChallengeCaptcha from './graphql/queries/challengeCaptcha.query.gql'
import ResendSignUpConfirmationConsumer from './graphql/queries/resendSignUpConfirmationByConsumer.query.gql'
import SignUpConfirmationByLink from './graphql/queries/signUpConfirmationByLink.query.gql'
import Moment from 'moment'

const url = process.env.VUE_APP_REGISTRY_URL
const apolloFederationUrl = process.env.VUE_APP_APOLLO_FEDERATION_URL

const client = new GraphQLClient(url)

Vue.use(Vuex)

export function getDefaultStore() {
  return {
    state: {
      accountId: '',
      loading: true,
      loadingId: false,
      loadingCaptcha: false,
      isExternalAuth: false,
      assets: {},
      fields: {
        legal: [],
        natural: []
      },
      terms: '',
      position: '',
      userInfo: {},
      isApp: false,
      access_token: null,
      hidden_email: null,
      hidden_phone: null,
      user_email: '',
      contacts: null,
      smsChannel: false,
      request_id: '',
      message_sent: false,
      cooldown: 0,
      is_recover_sms: false,
      facebook_pixel_id: '',
      google_analytics_id: '',
      allowed_countries: [],
      selectedCountry: 'BRASIL',
      socialMedia: {},
      login_menu: false,
      delete_request: false,
      is_confirmed: false,
      show_delete_buttons: false,
      allowed_person_types_signup: [],
      allowed_modules: {},
      hotsiteLinks: [],
      finishScreenWhatsapp: {
        whatsapp_link: '',
        enable_whatsapp_link: false
      },
      appStores: {
        enable_apps: false,
        app_store_link: '',
        play_store_link: ''
      }
    },
    actions: {
      async getClient({ commit, state }, hostname) {
        commit('LOADING', true)
        return new Promise((resolve, reject) => {
          client
            .request(Hotsite, {
              url: hostname
            })
            .then(data => {
              document.title = data.hotsite.name ? `Clube ${data.hotsite.name}` : 'Clube'
              client.setHeader('accountid', data.hotsite.accountId)
              commit('ACCOUNT_ID', data.hotsite.accountId)
              commit('TERMS', data.hotsite.terms)
              commit('SHOW_DELETE_BUTTONS', data.hotsite.delete_data_requests)
              commit('FIELDS', data.hotsite.configuration.fields)
              commit('ALLOWED_PERSON_TYPES_SIGNUP', data.hotsite.configuration.fields.allowed_person_types_signup)
              commit('ASSETS', data.hotsite.configuration.assets)
              commit('PIXEL', data.hotsite.configuration.facebook_pixel_id)
              commit('GOOGLE_ANALYTICS', data.hotsite.configuration.google_analytics_id)
              commit('ALLOWED_COUNTRIES', data.hotsite.configuration.allowed_countries)
              commit('LOGIN_MENU', data.hotsite.configuration.menu.login)
              commit('POSITION', data.hotsite.configuration.assets.positions.formImage || 'RIGHT')
              commit('CONTACTS', data.hotsite.contacts)
              commit('SMS_CHANNEL', data.hotsite.passwordRecoveryChannels.sms)
              commit('SOCIAL_MEDIA', data.hotsite.socialMedia)
              commit('ALLOWED_MODULES', data.hotsite.configuration.modules || {})
              commit('HOTSITE_LINKS', data.hotsite.hotsiteLinks)
              commit('APP_STORES', {
                enable_apps: data.hotsite.enable_apps,
                app_store_link: data.hotsite.appStoresLinks ? data.hotsite.appStoresLinks.app_store : '',
                play_store_link: data.hotsite.appStoresLinks ? data.hotsite.appStoresLinks.play_store : ''
              })
              commit('FINISH_SCREEN_WHATSAPP', { enable_whatsapp_link: data.hotsite.enable_whatsapp_link, whatsapp_link: data.hotsite.whatsapp_link })
              resolve()
            })
            .catch(err => {
              reject(err)
            })
            .finally(() => commit('LOADING', false))
        }).finally()
      },
      createUser({ commit, state }, info) {
        commit('LOADING', true)
        return new Promise((resolve, reject) => {
          client
            .request(CreateUser, info)
            .then(data => {
              resolve(data)
            })
            .catch(error => {
              reject(error)
            })
            .finally(() => {
              commit('LOADING', false)
            })
        })
      },
      challengeCaptcha({ commit }, params) {
        commit('LOADING_CAPTCHA', true)
        return new Promise(async (resolve, reject) => {
          try {
            let token
            if (params.version === 'V3') {
              token = await window.grecaptcha.execute(process.env.VUE_APP_RECAPTCHA_SITE_KEY_V3, { action: 'SIGNUP' })
            } else if (params.version === 'V2') {
              token = params.token
            }
            const data = await client.request(ChallengeCaptcha, { ...params, token, action: 'SIGNUP' })
            commit('LOADING_CAPTCHA', false)
            resolve(data)
          } catch (error) {
            commit('LOADING_CAPTCHA', false)
            reject(error)
          }
        })
      },
      checkUniqueUser({ commit }, value) {
        commit('LOADING_ID', true)
        return new Promise((resolve, reject) => {
          client
            .request(UserExists, value)
            .then(response => {
              commit('HIDDEN_EMAIL', response.userVerification.hidden_email)
              commit('HIDDEN_PHONE', response.userVerification.hidden_phone)
              commit('DELETE_REQUEST', response.userVerification.has_pending_delete_data_request)
              commit('SET_IS_CONFIRMED', response.userVerification.is_confirmed)
              resolve(response.userVerification)
            })
            .catch(error => {
              reject(error)
            })
        }).finally(() => {
          commit('LOADING_ID', false)
        })
      },
      login({ commit }, loginParams) {
        return new Promise((resolve, reject) => {
          client
            .request(Login, loginParams)
            .then(({ login }) => {
              commit('ACCESS_TOKEN', login.access_token)
              commit('USER_INFO', { ...login.user, birth_date: Moment(login.user.birth_date, 'YYYY-MM-DD').format('DD-MM-YYYY') })
              resolve()
            })
            .catch(error => {
              reject(error)
            })
        })
      },
      tokenLogin({ commit }, token) {
        return new Promise((resolve, reject) => {
          client
            .request(TokenLogin, token)
            .then(({ tokenLogin }) => {
              commit('ACCESS_TOKEN', tokenLogin.access_token)
              commit('USER_INFO', { ...tokenLogin.user, birth_date: Moment(tokenLogin.user.birth_date, 'YYYY-MM-DD').format('DD-MM-YYYY') })
              resolve()
            })
            .catch(error => {
              reject(error)
            })
        })
      },
      validatePassword({ commit }, loginParams) {
        return new Promise((resolve, reject) => {
          client
            .request(ValidatePassword, loginParams)
            .then(() => {
              resolve()
            })
            .catch(error => {
              reject(error)
            })
        })
      },
      updatePassword({ state }, params) {
        return new Promise((resolve, reject) => {
          client.setHeader('authorization', `Bearer ${state.access_token}`)
          client
            .request(UpdatePassword, params)
            .then(() => {
              client.setHeader('authorization', '')
              resolve()
            })
            .catch(err => {
              reject(err)
            })
        })
      },
      updateUser({ commit, state }, params) {
        commit('LOADING', true)
        return new Promise((resolve, reject) => {
          client.setHeader('authorization', `Bearer ${state.access_token}`)
          client
            .request(UpdateUser, params)
            .then(() => {
              client.setHeader('authorization', '')
              resolve()
            })
            .catch(err => {
              reject(err)
              commit('LOADING', false)
            })
        })
      },
      recoverPassword({ commit }, params) {
        return new Promise((resolve, reject) => {
          client
            .request(PasswordRecover, params)
            .then(() => {
              resolve()
            })
            .catch(err => {
              reject(err)
            })
        })
      },
      recoverPasswordSms({ commit, state }, params) {
        return new Promise((resolve, reject) => {
          client.setHeader('accountid', state.accountId)
          client
            .request(PasswordRecoverSms, params)
            .then(data => {
              if (data.requestPhoneValidation.request_id) commit('REQUEST_ID', data.requestPhoneValidation.request_id)
              commit('COOLDOWN', data.requestPhoneValidation.cooldown)
              commit('MESSAGE_SENT', data.requestPhoneValidation.message_sent)
              resolve()
            })
            .catch(err => {
              reject(err)
            })
        })
      },
      resetPassword({ commit }, params) {
        return new Promise((resolve, reject) => {
          client
            .request(ResetPassword, params)
            .then(({ resetPassword }) => {
              commit('ACCESS_TOKEN', resetPassword.access_token)
              commit('USER_INFO', { ...resetPassword.user, birth_date: Moment(resetPassword.user.birth_date, 'YYYY-MM-DD').format('DD-MM-YYYY') })
              resolve(resetPassword.user.document_no)
            })
            .catch(err => {
              reject(err)
            })
        })
      },
      resetPasswordBySms({ commit, state }, params) {
        return new Promise((resolve, reject) => {
          client.setHeader('authorization', `Bearer ${state.access_token}`)
          client.setHeader('accountid', state.accountId)
          client
            .request(ResetPasswordSms, params)
            .then(() => {
              resolve()
            })
            .catch(err => {
              reject(err)
            })
        })
      },
      setIsApp({ commit }, params) {
        commit('SET_IS_APP', params)
      },
      tokenValidation({ commit }, params) {
        commit('LOADING', true)
        return new Promise((resolve, reject) => {
          client
            .request(TokenValidation, params)
            .then(() => {
              resolve()
            })
            .catch(err => {
              reject(err)
            })
            .finally(() => {
              setTimeout(() => {
                commit('LOADING', false)
              }, 500)
            })
        })
      },
      validatePin({ commit, state }, params) {
        return new Promise((resolve, reject) => {
          client.setHeader('accountid', state.accountId)
          client
            .request(PhoneValidation, { ...params, request_id: state.request_id })
            .then(data => {
              const login = data.phoneValidation
              if (login) {
                commit('IS_RECOVER_SMS', true)
                commit('ACCESS_TOKEN', login.access_token)
                commit('USER_INFO', { ...login.user, birth_date: Moment(login.user.birth_date, 'YYYY-MM-DD').format('DD-MM-YYYY') })
              }
              resolve()
            })
            .catch(err => {
              reject(err)
            })
        })
      },
      createDeleteDataRequest({ commit, state }, params) {
        return new Promise((resolve, reject) => {
          client.setHeader('authorization', `Bearer ${state.access_token}`)
          client.setHeader('accountid', state.accountId)
          client
            .request(CreateDeleteDataRequest, params)
            .then(() => {
              resolve()
            })
            .catch(err => {
              reject(err)
            })
        })
      },
      async resendSignUpValidationByConsumer({ commit, state }, params) {
        try {
          const hasSend = await client.request(ResendSignUpConfirmationConsumer, params)
          return hasSend
        } catch (err) {
          throw err
        }
      },
      async signUpConfirmationByLink({ commit, state }, { code }) {
        const apolloFederationClient = new GraphQLClient(apolloFederationUrl, {
          headers: {
            accountid: state.accountId,
            'auth-shortcode': code
          }
        })

        try {
          const { signUpConfirmationByLink } = await apolloFederationClient.request(SignUpConfirmationByLink)
          return signUpConfirmationByLink
        } catch (err) {
          throw err
        }
      },
      setIsExternalAuth({ commit }, params) {
        commit('SET_IS_EXTERNAL_AUTH', params)
      }
    },
    getters: {
      GET_ACCOUNT_ID: state => {
        return state.accountId
      },
      GET_FIELDS: state => {
        const legalFields = state.fields.legal.map(field => {
          if (state.selectedCountry !== 'BRASIL') {
            if (field.name === 'phone') {
              const mask = state.selectedCountry === 'ARGENTINA' ? ['+54 ############'] : ['+598 ############']
              return { ...field, mask, validators: { required: true } }
            }
            if (field.name === 'birth_date') {
              return { ...field }
            }
            return { ...field, mask: null }
          }
          return { ...field }
        })
        const naturalFields = state.fields.natural.map(field => {
          if (state.selectedCountry !== 'BRASIL') {
            if (field.name === 'phone') {
              const mask = state.selectedCountry === 'ARGENTINA' ? ['+54 ############'] : ['+598 ############']
              return { ...field, mask, validators: { required: true } }
            }
            if (field.name === 'birth_date') {
              return { ...field }
            }
            return { ...field, mask: null }
          }
          return { ...field }
        })
        return {
          allowed_person_types_signup: [...state.fields.allowed_person_types_signup],
          legal: [...legalFields],
          natural: [...naturalFields]
        }
      },
      GET_ALLOWED_PERSON_TYPES_SIGNUP: state => {
        return state.allowed_person_types_signup
      },
      GET_IS_NATURAL_PERSON: state => {
        return state.USER_INFO ? state.USER_INFO.person_type === 'N' : false
      },
      GET_ASSETS: state => {
        return state.assets
      },
      GET_TERMS: state => {
        return state.terms
      },
      GET_POSITION: state => {
        return state.position
      },
      LOADING: state => {
        return state.loading
      },
      LOADING_ID: state => {
        return state.loadingId
      },
      LOADING_CAPTCHA: state => {
        return state.loadingCaptcha
      },
      USER_INFO: state => {
        return state.userInfo
      },
      TOKEN: state => {
        return state.access_token
      },
      IS_APP: state => {
        return state.isApp
      },
      HIDDEN_EMAIL: state => {
        return state.hidden_email
      },
      HIDDEN_PHONE: state => {
        return state.hidden_phone
      },
      DELETE_REQUEST: state => {
        return state.delete_request
      },
      IS_CONFIRMED: state => {
        return state.is_confirmed
      },
      USER_EMAIL: state => {
        return state.user_email
      },
      CONTACTS: state => {
        return state.contacts
      },
      PIXEL(state) {
        return state.facebook_pixel_id
      },
      GOOGLE_ANALYTICS(state) {
        return state.google_analytics_id
      },
      ALLOWED_COUNTRIES(state) {
        return state.allowed_countries
      },
      SELECTED_COUNTRY(state) {
        return state.selectedCountry
      },
      SMS_CHANNEL: state => {
        return state.smsChannel
      },
      COOLDOWN: state => {
        return state.cooldown
      },
      MESSAGE_SENT: state => {
        return state.message_sent
      },
      IS_RECOVER_SMS: state => {
        return state.is_recover_sms
      },
      SOCIAL_MEDIA: state => {
        return state.socialMedia
      },
      LOGIN_MENU: state => {
        return state.login_menu
      },
      SHOW_DELETE_BUTTONS: state => {
        return state.show_delete_buttons
      },
      ALLOWED_MODULES: state => {
        return state.allowed_modules
      },
      FINISH_SCREEN_WHATSAPP: state => {
        return state.finishScreenWhatsapp
      },
      APP_STORES: state => {
        return state.appStores
      },
      IS_EXTERNAL_AUTH: state => {
        return state.isExternalAuth
      }
    },
    mutations: {
      SET_IS_APP(state, isApp) {
        state.isApp = isApp
      },
      LOADING(state, payload) {
        state.loading = payload
      },
      LOADING_ID(state, payload) {
        state.loadingId = payload
      },
      LOADING_CAPTCHA(state, payload) {
        state.loadingCaptcha = payload
      },
      ASSETS(state, payload) {
        state.assets = payload
      },
      FIELDS(state, payload) {
        state.fields = payload
      },
      ALLOWED_PERSON_TYPES_SIGNUP(state, payload) {
        state.allowed_person_types_signup = payload
      },
      TERMS(state, payload) {
        state.terms = payload
      },
      POSITION(state, payload) {
        state.position = payload
      },
      ACCOUNT_ID(state, payload) {
        state.accountId = payload
      },
      USER_INFO(state, payload) {
        state.userInfo = payload
      },
      ACCESS_TOKEN(state, payload) {
        state.access_token = payload
      },
      HIDDEN_EMAIL(state, payload) {
        state.hidden_email = payload
      },
      HIDDEN_PHONE(state, payload) {
        state.hidden_phone = payload
      },
      DELETE_REQUEST(state, payload) {
        state.delete_request = payload
      },
      SET_IS_CONFIRMED(state, payload) {
        state.is_confirmed = payload
      },
      USER_EMAIL(state, payload) {
        state.user_email = payload
      },
      CONTACTS(state, payload) {
        state.contacts = payload
      },
      SMS_CHANNEL(state, payload) {
        state.smsChannel = payload
      },
      REQUEST_ID(state, payload) {
        state.request_id = payload
      },
      COOLDOWN(state, payload) {
        state.cooldown = payload
      },
      MESSAGE_SENT(state, payload) {
        state.message_sent = payload
      },
      IS_RECOVER_SMS(state, payload) {
        state.is_recover_sms = payload
      },
      PIXEL(state, payload) {
        state.facebook_pixel_id = payload
      },
      GOOGLE_ANALYTICS(state, payload) {
        state.google_analytics_id = payload
      },
      ALLOWED_COUNTRIES(state, payload) {
        state.allowed_countries = payload
      },
      SELECTED_COUNTRY(state, payload) {
        state.selectedCountry = payload
      },
      SOCIAL_MEDIA(state, payload) {
        state.socialMedia = payload
      },
      LOGIN_MENU(state, payload) {
        state.login_menu = payload
      },
      SHOW_DELETE_BUTTONS(state, payload) {
        state.show_delete_buttons = payload
      },
      ALLOWED_MODULES(state, payload) {
        state.allowed_modules = payload
      },
      HOTSITE_LINKS(state, payload) {
        state.hotsiteLinks = payload
      },
      FINISH_SCREEN_WHATSAPP(state, payload) {
        state.finishScreenWhatsapp = payload
      },
      APP_STORES(state, payload) {
        state.appStores = payload
      },
      SET_IS_EXTERNAL_AUTH(state, payload) {
        state.isExternalAuth = payload
      }
    }
  }
}

export default new Vuex.Store(getDefaultStore())
